'use client';

import React from 'react';
import { HStack, Text, Box } from '@skin-seoul/design-system';
import { SafeLink } from '@/components/ui/SafeLink';
import LucideIcon from '@/components/ui/LucideIcon';
import type { Category } from '@skin-seoul/service';
import { unescape } from 'es-toolkit/string';
import { CLASSNAME, useProductService, useRegionCurrency } from '@skin-seoul/service';
import { useParams } from 'next/navigation';
interface BreadcrumbItem {
  name: string;
  path: string;
  isActive?: boolean;
}
const ProductBreadcrumb = () => {
  const params = useParams();
  const {
    useGetProductsDetail
  } = useProductService();
  const {
    currency
  } = useRegionCurrency();
  const {
    data: {
      categories
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const getCategoryPath = (categories: Category[]) => {
    const breadcrumbItems: BreadcrumbItem[] = [];
    const categoryMap = new Map<number, Category>();
    const allCategories: Category[] = [];
    categories.forEach(category => {
      categoryMap.set(category.termId, category);
      allCategories.push(category);
      if (category.categoryHierarchy && category.categoryHierarchy.length > 0) {
        category.categoryHierarchy.forEach(hierarchy => {
          const subCategory: Category = {
            termId: hierarchy.termId,
            name: hierarchy.name,
            slug: hierarchy.slug,
            taxonomy: hierarchy.taxonomy,
            description: '',
            count: 0,
            parentTermId: category.termId,
            categoryHierarchy: hierarchy.categoryHierarchy || [],
            rankInTerm: 0
          };
          categoryMap.set(hierarchy.termId, subCategory);
          allCategories.push(subCategory);
        });
      }
    });
    categories.forEach(category => {
      const path: BreadcrumbItem[] = [];
      let currentCategory: Category | null = category;
      const visitedIds = new Set<number>();
      const categoryChain: Category[] = [];
      while (currentCategory && !visitedIds.has(currentCategory.termId)) {
        visitedIds.add(currentCategory.termId);
        categoryChain.unshift(currentCategory);
        if (currentCategory.parentTermId && currentCategory.parentTermId !== 0) {
          const parentCategory = categoryMap.get(currentCategory.parentTermId);
          currentCategory = parentCategory || null;
        } else {
          currentCategory = null;
        }
      }
      let currentPath = '';
      categoryChain.forEach(cat => {
        currentPath = currentPath ? `${currentPath}/${cat.slug}` : cat.slug;
        path.push({
          name: unescape(cat.name),
          path: `/collections/${currentPath}`
        });
      });
      breadcrumbItems.push(...path);
    });
    const uniqueItems = breadcrumbItems.filter((item, index, self) => index === self.findIndex(t => t.path === item.path));
    return uniqueItems;
  };
  const categoryPath = getCategoryPath(categories);
  const breadcrumbItems: BreadcrumbItem[] = [{
    name: 'Home',
    path: '/'
  }, ...categoryPath];
  return <Box background="white" paddingY={{
    base: 3,
    tablet: 5
  }} width="100%" data-sentry-element="Box" data-sentry-component="ProductBreadcrumb" data-sentry-source-file="ProductBreadcrumb.tsx">
      <Box marginX="auto" maxWidth="1260px" paddingX={4} data-sentry-element="Box" data-sentry-source-file="ProductBreadcrumb.tsx">
        <HStack gap={{
        base: 1,
        tablet: 2
      }} alignItems="left" justifyContent="left" data-sentry-element="HStack" data-sentry-source-file="ProductBreadcrumb.tsx">
          {breadcrumbItems.map((item, index) => <HStack key={index} gap={{
          base: 1,
          tablet: 2
        }} alignItems="center">
              <SafeLink href={item.path}>
                <Text textStyle={{
              base: 'label.small',
              tablet: 'label.large'
            }} fontWeight="normal" color="neutral.400" textAlign="left" whiteSpace="nowrap" className={CLASSNAME.NOT_TRANSLATE} _hover={{
              color: 'neutral.600'
            }}>
                  {item.name}
                </Text>
              </SafeLink>

              {index < breadcrumbItems.length - 1 && <Box>
                  <LucideIcon name="ChevronRight" size={{
              base: 'sm',
              tablet: 'md'
            }} color="neutral.400" />
                </Box>}
            </HStack>)}
        </HStack>
      </Box>
    </Box>;
};
export default ProductBreadcrumb;